/**
 * Buyer checklist view model (Milestone 12).
 */
import {
  CHECKLIST_GUIDE_HREFS,
  CHECKLIST_PREVIEW_COPY_ES,
  CHECKLIST_PRIORITY,
  CHECKLIST_CATEGORIES,
  CHECKLIST_IMPORTANCE,
} from "../constants/buyerChecklistConstants";
import { FAIR_PRICE_LABELS } from "../constants/automapFairPriceConstants";
import { RISK_LEVELS } from "../constants/automapVehicleRiskConstants";
import { BUYER_CHECKLIST_ITEMS } from "../data/buyerChecklistContent";
import { buildContextualChecklistItems } from "./buyerChecklistContextual";
import { getDealerTrustViewModel } from "./dealerTrustViewModel";
import { getFairPriceViewModel } from "./fairPriceViewModel";
import { getVehicleRiskViewModel } from "./vehicleRiskViewModel";
import { formatVehicleTitle } from "./automapListing/listingFormatters";

const PHASE_CATEGORY_MAP = {
  before_contact: CHECKLIST_CATEGORIES.DISPONIBILIDAD,
  before_visit: CHECKLIST_CATEGORIES.CONCESIONARIO,
  inspection: CHECKLIST_CATEGORIES.PERITAJE,
  documents: CHECKLIST_CATEGORIES.DOCUMENTOS,
  negotiation: CHECKLIST_CATEGORIES.PRECIO,
  payment: CHECKLIST_CATEGORIES.PAGOS,
  transfer: CHECKLIST_CATEGORIES.TRASPASO,
  after_purchase: CHECKLIST_CATEGORIES.SEGURO_SOAT,
};

const IMPORTANCE_PRIORITY_MAP = {
  [CHECKLIST_IMPORTANCE.ESSENTIAL]: CHECKLIST_PRIORITY.HIGH,
  [CHECKLIST_IMPORTANCE.RECOMMENDED]: CHECKLIST_PRIORITY.MEDIUM,
  [CHECKLIST_IMPORTANCE.OPTIONAL]: CHECKLIST_PRIORITY.LOW,
};

export function enrichChecklistItem(item) {
  if (!item) return null;
  return {
    ...item,
    key: item.key || item.id,
    category: item.category || PHASE_CATEGORY_MAP[item.phase] || CHECKLIST_CATEGORIES.DOCUMENTOS,
    shortTitle: item.shortTitle || item.title,
    question: item.question || item.description,
    whyItMatters: item.whyItMatters || item.description,
    priority: item.priority || IMPORTANCE_PRIORITY_MAP[item.importance] || CHECKLIST_PRIORITY.MEDIUM,
    ctaLabel: item.ctaLabel || null,
    guideHref: item.guideHref || item.links?.[0]?.href || null,
  };
}

export function buildChecklistGuideHref(listing, listingId) {
  const params = new URLSearchParams();
  if (listingId) params.set("listingId", listingId);
  if (listing?.slug) params.set("listingSlug", listing.slug);
  if (listing?.make) params.set("make", listing.make);
  if (listing?.model) params.set("model", listing.model);
  if (listing?.year) params.set("year", String(listing.year));
  if (listing?.priceCop != null) params.set("priceCop", String(listing.priceCop));
  if (listing?.riskLevel) params.set("riskLevel", listing.riskLevel);
  if (listing?.fairPriceLabel) params.set("fairPriceLabel", listing.fairPriceLabel);
  const qs = params.toString();
  return qs ? `${CHECKLIST_GUIDE_HREFS.hub}?${qs}` : CHECKLIST_GUIDE_HREFS.hub;
}

export function buildChecklistSummary(listing, riskVm, fairPriceVm, dealerVm) {
  const parts = [];
  if (listing?.make && listing?.model) {
    parts.push(formatVehicleTitle(listing));
  }
  if (riskVm?.level && riskVm.level !== RISK_LEVELS.LOW) {
    parts.push(riskVm.shortLabel || riskVm.displayLabel);
  }
  if (fairPriceVm?.displayLabel && fairPriceVm.hasComparableData) {
    parts.push(fairPriceVm.displayLabel);
  }
  if (dealerVm && !dealerVm.isVerified && dealerVm.verificationStatus !== "unknown") {
    parts.push(dealerVm.verificationLabel);
  }
  return parts.length ? parts.join(" · ") : "Checklist general para comprar carro usado";
}

export function shouldShowCompareSimilarCta(listing, riskVm, fairPriceVm) {
  const label = listing?.fairPriceLabel || fairPriceVm?.label;
  const riskLevel = riskVm?.level || listing?.riskLevel;
  return (
    [FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high].includes(label) ||
    [RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(riskLevel)
  );
}

export function getCompareSimilarLabel(listing, riskVm, fairPriceVm, t = null) {
  const label = listing?.fairPriceLabel || fairPriceVm?.label;
  if (t) {
    if ([FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high].includes(label)) {
      return t("automotive.detail.checklistUi.compareSimilar");
    }
    return t("automotive.detail.checklistUi.viewSimilar");
  }
  if ([FAIR_PRICE_LABELS.high_price, FAIR_PRICE_LABELS.slightly_high].includes(label)) {
    return CHECKLIST_PREVIEW_COPY_ES.compareSimilar;
  }
  return CHECKLIST_PREVIEW_COPY_ES.viewSimilar;
}

export function buildVehicleDetailChecklist(listing, options = {}) {
  const listingId = options.listingId || listing?.id || listing?.vehicleListingId || null;
  const { t = null, locale = "es-CO" } = options;
  const riskVm = getVehicleRiskViewModel(listing, { t });
  const fairPriceVm = getFairPriceViewModel(listing, { t, locale });
  const dealerVm = getDealerTrustViewModel(listing, options.dealer || null, { slug: options.slug || "automap" });

  const contextual = buildContextualChecklistItems(listing, {
    limit: options.limit || 6,
    riskVm,
    fairPriceVm,
    dealerVm,
  }).map(enrichChecklistItem);

  const highPriorityItems = contextual.filter((item) => item.priority === CHECKLIST_PRIORITY.HIGH);
  const recommendedItems = contextual.filter((item) => item.priority !== CHECKLIST_PRIORITY.HIGH);

  return {
    highPriorityItems: highPriorityItems.length ? highPriorityItems : contextual.slice(0, 4),
    recommendedItems,
    allItems: contextual,
    checklistSummary: buildChecklistSummary(listing, riskVm, fairPriceVm, dealerVm),
    guideHref: buildChecklistGuideHref(listing, listingId),
    showCompareSimilar: shouldShowCompareSimilarCta(listing, riskVm, fairPriceVm),
    compareSimilarLabel: getCompareSimilarLabel(listing, riskVm, fairPriceVm, t),
    riskVm,
    fairPriceVm,
    dealerVm,
  };
}

export function getBuyerChecklistForListing(listing, options = {}) {
  return buildVehicleDetailChecklist(listing, options);
}

export function getContextualChecklistItems(listing, options = {}) {
  return buildVehicleDetailChecklist(listing, options).allItems;
}

export function getGeneralBuyerChecklist() {
  return BUYER_CHECKLIST_ITEMS.map(enrichChecklistItem);
}

export function listingFromChecklistQueryParams(searchParams) {
  if (!searchParams) return null;
  const listingId = searchParams.get("listingId");
  const make = searchParams.get("make");
  const model = searchParams.get("model");
  if (!listingId && !make && !model) return null;
  return {
    id: listingId,
    slug: searchParams.get("listingSlug") || listingId,
    make,
    model,
    year: searchParams.get("year") ? Number(searchParams.get("year")) : null,
    priceCop: searchParams.get("priceCop") ? Number(searchParams.get("priceCop")) : null,
    riskLevel: searchParams.get("riskLevel") || null,
    fairPriceLabel: searchParams.get("fairPriceLabel") || null,
    dealerName: searchParams.get("dealerName") || null,
  };
}
